import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import { AlertCircle, CheckCircle, BarChart3, Loader, Star, ChevronDown, ChevronRight, Package, FolderOpen } from 'lucide-react';

const MAGENTO_PRODUCT_TYPES = [
  { value: 'simple', label: 'Simple' },
  { value: 'configurable', label: 'Configurable' },
  { value: 'grouped', label: 'Grouped' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'downloadable', label: 'Downloadable' },
];

const ProductTable = ({ products, siteId, sectionKey, icon: Icon, iconColor, title, countLabel, isSectionExpanded, toggleSection }) => {
  if (!products?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      <button
        onClick={() => toggleSection(siteId, sectionKey)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className={`h-5 w-5 ${iconColor}`} />
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{products.length} {countLabel}</p>
          </div>
        </div>
        {isSectionExpanded(siteId, sectionKey) ? (
          <ChevronDown className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {isSectionExpanded(siteId, sectionKey) && (
        <div className="border-t border-gray-200 p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{product.sku}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{product.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 capitalize">{product.type_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{product.id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ContentInsights = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [contentData, setContentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedType, setSelectedType] = useState('');
  const [includeInvisible, setIncludeInvisible] = useState(false);
  const [includeDisabled, setIncludeDisabled] = useState(false);

  useEffect(() => {
    setContentData([]);
    setError(null);
    setSelectedType('');
    setIncludeInvisible(false);
    setIncludeDisabled(false);
  }, [user, selectedSite]);

  const fetchContentData = async (typeId, invisible = includeInvisible, disabled = includeDisabled) => {
    setLoading(true);
    setError(null);
    setContentData([]);

    try {
      const params = new URLSearchParams({ siteId: selectedSite.id });
      if (typeId) params.set('typeId', typeId);
      if (invisible) params.set('includeInvisible', '1');
      if (disabled) params.set('includeDisabled', '1');

      const response = await fetch(
        `/app/api/analytics/content-insights.php?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setContentData(result.data);
      } else {
        setError(result.message || 'Failed to fetch content insights');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-50';
    if (score >= 60) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  const getPercentage = (current, total) => {
    if (total === 0) return 0;
    return Math.round((current / total) * 100);
  };

  const toggleSection = (siteId, section) => {
    setExpandedSections(prev => ({
      ...prev,
      [`${siteId}-${section}`]: !prev[`${siteId}-${section}`]
    }));
  };

  const isSectionExpanded = (siteId, section) => {
    return expandedSections[`${siteId}-${section}`] || false;
  };

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    setExpandedSections({});
    fetchContentData(typeId, includeInvisible, includeDisabled);
  };

  const handleInvisibleToggle = (checked) => {
    setIncludeInvisible(checked);
    if (selectedType) fetchContentData(selectedType, checked, includeDisabled);
  };

  const handleDisabledToggle = (checked) => {
    setIncludeDisabled(checked);
    if (selectedType) fetchContentData(selectedType, includeInvisible, checked);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-8 w-8 text-blue-600" />
            Content Insights
          </h1>
          <p className="text-gray-600 mt-2">Analyze product descriptions, meta tags, categories, and customer reviews</p>
        </div>

        {/* Product type selector */}
        {!selectedSite ? (
          <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">Please select a site</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Select product type to analyse</h2>
            <div className="flex flex-wrap gap-3">
              {MAGENTO_PRODUCT_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => handleTypeSelect(type.value)}
                  disabled={loading}
                  className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                    selectedType === type.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {type.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 mt-4 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={includeInvisible}
                onChange={e => handleInvisibleToggle(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Include products not visible individually</span>
            </label>
            <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={includeDisabled}
                onChange={e => handleDisabledToggle(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Include disabled products</span>
            </label>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading {MAGENTO_PRODUCT_TYPES.find(t => t.value === selectedType)?.label} products...</p>
            </div>
          </div>
        )}

        {/* Error (not shown when no site — handled above) */}
        {error && !loading && selectedSite && (
          <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3 mb-6">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!loading && contentData.length > 0 && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-600">Content Quality Score</p>
                <div className="flex items-center gap-2 mt-2">
                  <p className={`text-3xl font-bold ${getScoreColor(
                    Math.round(contentData.filter(d => !d.error).reduce((sum, d) => sum + d.content_score, 0) / contentData.filter(d => !d.error).length)
                  )}`}>
                    {Math.round(contentData.filter(d => !d.error).reduce((sum, d) => sum + d.content_score, 0) / contentData.filter(d => !d.error).length)}
                  </p>
                  <span className="text-gray-600">/100</span>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-600">Total Products</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {contentData.reduce((sum, d) => sum + (d.total_products || 0), 0)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <p className="text-sm text-gray-600">Total Reviews</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {contentData.reduce((sum, d) => sum + (d.total_reviews || 0), 0)}
                </p>
              </div>
            </div>

            {/* Detailed Content Analysis */}
            <div className="space-y-6">
              {contentData.map((site) => (
                <div key={site.site_id} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 rounded-lg ${getScoreBgColor(site.content_score)} flex items-center justify-center`}>
                          <span className={`text-2xl font-bold ${getScoreColor(site.content_score)}`}>
                            {site.content_score}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{site.site_name}</h3>
                          <p className="text-sm text-gray-500">Content Quality Score</p>
                        </div>
                      </div>
                      {site.error ? (
                        <AlertCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                  </div>

                  <div className="p-6">
                    {site.error ? (
                      <div className="rounded-lg bg-red-50 p-4 flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-900">Error</p>
                          <p className="text-sm text-red-700">{site.error}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Product Content */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-gray-900">Product Content</h4>

                          <div className="p-4 bg-blue-50 rounded border border-blue-200">
                            <p className="text-xs text-blue-600 uppercase font-semibold mb-1">Total Products</p>
                            <p className="text-2xl font-bold text-blue-900">{site.total_products}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-600">Descriptions</span>
                              <span className="text-xs font-semibold text-gray-900">
                                {getPercentage(site.products_with_descriptions, site.total_products)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${getPercentage(site.products_with_descriptions, site.total_products)}%` }}></div>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{site.products_with_descriptions} / {site.total_products}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-600">Meta Titles</span>
                              <span className="text-xs font-semibold text-gray-900">
                                {getPercentage(site.products_with_meta_titles, site.total_products)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${getPercentage(site.products_with_meta_titles, site.total_products)}%` }}></div>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{site.products_with_meta_titles} / {site.total_products}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-600">Meta Descriptions</span>
                              <span className="text-xs font-semibold text-gray-900">
                                {getPercentage(site.products_with_meta_descriptions, site.total_products)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${getPercentage(site.products_with_meta_descriptions, site.total_products)}%` }}></div>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{site.products_with_meta_descriptions} / {site.total_products}</p>
                          </div>
                        </div>

                        {/* Category Content */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold text-gray-900">Category Content</h4>

                          <div className="p-4 bg-green-50 rounded border border-green-200">
                            <p className="text-xs text-green-600 uppercase font-semibold mb-1">Total Categories</p>
                            <p className="text-2xl font-bold text-green-900">{site.total_categories}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-600">With Descriptions</span>
                              <span className="text-xs font-semibold text-gray-900">
                                {getPercentage(site.categories_with_descriptions, site.total_categories)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${getPercentage(site.categories_with_descriptions, site.total_categories)}%` }}></div>
                            </div>
                            <p className="text-xs text-gray-600 mt-1">{site.categories_with_descriptions} / {site.total_categories}</p>
                          </div>
                        </div>

                        {/* Customer Reviews */}
                        <div className="space-y-4 relative">
                          <h4 className="text-sm font-semibold text-gray-900">Customer Reviews</h4>

                          <div className={`relative ${!site.reviews_enabled ? 'opacity-50' : ''}`}>
                            <div className="p-4 bg-amber-50 rounded border border-amber-200">
                              <p className="text-xs text-amber-600 uppercase font-semibold mb-1">Total Reviews</p>
                              <p className="text-2xl font-bold text-amber-900">{site.total_reviews}</p>
                            </div>

                            <div className="mt-4 p-4 bg-purple-50 rounded border border-purple-200">
                              <p className="text-xs text-purple-600 uppercase font-semibold mb-1">Average Rating</p>
                              <div className="flex items-center gap-1 mt-2">
                                <span className="text-2xl font-bold text-purple-900">{site.average_rating}</span>
                                <span className="text-xs text-purple-700">/5</span>
                              </div>
                              <div className="flex items-center gap-1 mt-2">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-4 w-4 ${i < Math.round(site.average_rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                                  />
                                ))}
                              </div>
                            </div>

                            {!site.reviews_enabled && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-5 rounded-lg">
                                <div className="bg-white px-4 py-3 rounded-lg shadow-lg border-2 border-gray-300">
                                  <p className="text-sm font-semibold text-gray-900">Reviews & Ratings are Disabled</p>
                                  <p className="text-xs text-gray-600 mt-1">Enable reviews in Magento to collect customer feedback</p>
                                </div>
                              </div>
                            )}
                          </div>

                          <div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              site.reviews_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {site.reviews_enabled ? '✓ Reviews Enabled' : '✗ Reviews Disabled'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Missing Content Details */}
            {contentData.map((site) => (
              !site.error && (
                <div key={`missing-${site.site_id}`} className="mt-8 space-y-4">
                  <ProductTable
                    products={site.products_missing_descriptions}
                    siteId={site.site_id}
                    sectionKey="missing-descriptions"
                    icon={Package}
                    iconColor="text-red-600"
                    title="Products Missing Descriptions"
                    countLabel="products need descriptions"
                    isSectionExpanded={isSectionExpanded}
                    toggleSection={toggleSection}
                  />
                  <ProductTable
                    products={site.products_missing_meta_titles}
                    siteId={site.site_id}
                    sectionKey="missing-meta-titles"
                    icon={Package}
                    iconColor="text-orange-600"
                    title="Products Missing Meta Titles"
                    countLabel="products need meta titles"
                    isSectionExpanded={isSectionExpanded}
                    toggleSection={toggleSection}
                  />
                  <ProductTable
                    products={site.products_missing_meta_descriptions}
                    siteId={site.site_id}
                    sectionKey="missing-meta-descriptions"
                    icon={Package}
                    iconColor="text-yellow-600"
                    title="Products Missing Meta Descriptions"
                    countLabel="products need meta descriptions"
                    isSectionExpanded={isSectionExpanded}
                    toggleSection={toggleSection}
                  />

                  {/* Categories Missing Descriptions */}
                  {site.categories_missing_descriptions?.length > 0 && (
                    <div className="bg-white rounded-lg shadow border border-gray-200">
                      <button
                        onClick={() => toggleSection(site.site_id, 'missing-category-descriptions')}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <FolderOpen className="h-5 w-5 text-purple-600" />
                          <div className="text-left">
                            <h3 className="text-lg font-semibold text-gray-900">Categories Missing Descriptions</h3>
                            <p className="text-sm text-gray-500">{site.categories_missing_descriptions.length} categories need descriptions</p>
                          </div>
                        </div>
                        {isSectionExpanded(site.site_id, 'missing-category-descriptions') ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      {isSectionExpanded(site.site_id, 'missing-category-descriptions') && (
                        <div className="border-t border-gray-200 p-6">
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category Name</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {site.categories_missing_descriptions.map((category, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{category.name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{category.level}</td>
                                    <td className="px-4 py-3 text-sm text-gray-500">{category.id}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            ))}

            {/* Content Optimization Tips */}
            <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Content Optimization Tips</h3>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Ensure all products have unique, detailed descriptions (aim for 100%)</li>
                <li>• Add meta titles and descriptions to improve search engine visibility</li>
                <li>• Complete category descriptions to improve navigation and SEO</li>
                <li>• Enable and encourage customer reviews - they boost trust and provide fresh content</li>
                <li>• Keep product URLs simple and descriptive</li>
                <li>• Use high-quality images and alt text for all products</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ContentInsights;
